/**
 * 部首 — radical 105 ตัว (Phase 0)
 * ย้ายจาก bushu-drill.html · รายชื่ออ้างอิง NOTES.md §10
 *
 * v0.2 จะเสริมคันจิตัวอย่างอัตโนมัติจาก radkfile (ดู DATA-SOURCES.md)
 * ตอนนี้ใช้ตัวอย่างที่คัดมือไว้ก่อน — คัดแล้วตรงกว่าสุ่มจากไฟล์
 */
import type { Card } from '@/core/types'
import type { Deck, Group } from './types'

/** [ตัวหลัก, รูปแปร, ความหมายไทย, วัน, ตัวอย่าง [คันจิ, ความหมาย][]] */
type Row = readonly [string, string, string, number, ReadonlyArray<readonly [string, string]>]

const DATA: readonly Row[] = [
  // Day 1 — ธรรมชาติพื้นฐาน
  ['人', '亻', 'คน', 1, [['休', 'พัก'], ['体', 'ร่างกาย']]],
  ['日', '', 'ดวงอาทิตย์ / วัน', 1, [['明', 'สว่าง'], ['時', 'เวลา']]],
  ['月', '', 'ดวงจันทร์ / เดือน', 1, [['朝', 'เช้า'], ['服', 'เสื้อผ้า']]],
  ['木', '', 'ต้นไม้', 1, [['林', 'ป่าละเมาะ'], ['森', 'ป่าใหญ่']]],
  ['水', '氵', 'น้ำ', 1, [['海', 'ทะเล'], ['泳', 'ว่ายน้ำ']]],
  ['火', '灬', 'ไฟ', 1, [['焼', 'เผา'], ['熱', 'ร้อน']]],
  ['土', '', 'ดิน', 1, [['地', 'พื้นดิน'], ['場', 'สถานที่']]],
  ['金', '', 'โลหะ / ทอง', 1, [['銀', 'เงิน'], ['鉄', 'เหล็ก']]],
  ['山', '', 'ภูเขา', 1, [['岩', 'หินผา'], ['島', 'เกาะ']]],
  ['川', '', 'แม่น้ำ', 1, [['州', 'รัฐ'], ['順', 'ลำดับ']]],
  ['田', '', 'นาข้าว', 1, [['町', 'เมือง'], ['男', 'ผู้ชาย']]],
  ['石', '', 'หิน', 1, [['砂', 'ทราย'], ['破', 'ทำลาย']]],
  ['雨', '', 'ฝน', 1, [['雪', 'หิมะ'], ['電', 'ไฟฟ้า']]],
  ['大', '', 'ใหญ่', 1, [['天', 'ท้องฟ้า'], ['太', 'อ้วน']]],
  ['小', '', 'เล็ก', 1, [['少', 'น้อย'], ['省', 'ประหยัด']]],

  // Day 2 — ร่างกาย
  ['口', '', 'ปาก / ช่อง', 2, [['味', 'รส'], ['品', 'สินค้า']]],
  ['目', '', 'ตา', 2, [['見', 'เห็น'], ['眠', 'หลับ']]],
  ['耳', '', 'หู', 2, [['聞', 'ฟัง'], ['取', 'หยิบ']]],
  ['手', '扌', 'มือ', 2, [['持', 'ถือ'], ['打', 'ตี']]],
  ['足', '', 'เท้า', 2, [['路', 'ถนน'], ['跳', 'กระโดด']]],
  ['心', '忄', 'หัวใจ / ใจ', 2, [['思', 'คิด'], ['情', 'อารมณ์']]],
  ['頁', '', 'หัว / หน้ากระดาษ', 2, [['顔', 'ใบหน้า'], ['頭', 'หัว']]],
  ['首', '', 'คอ', 2, [['道', 'ถนน'], ['導', 'นำทาง']]],
  ['身', '', 'ร่างกาย', 2, [['射', 'ยิง'], ['躍', 'กระโดดโลดเต้น']]],
  ['骨', '', 'กระดูก', 2, [['滑', 'ลื่น']]],
  ['毛', '', 'ขน', 2, [['尾', 'หาง'], ['耗', 'สึกหรอ']]],
  ['血', '', 'เลือด', 2, [['衆', 'มวลชน']]],
  ['女', '', 'ผู้หญิง', 2, [['好', 'ชอบ'], ['姉', 'พี่สาว']]],
  ['子', '', 'เด็ก', 2, [['学', 'เรียน'], ['字', 'ตัวอักษร']]],
  ['父', '', 'พ่อ', 2, [['爺', 'ตา / คุณปู่']]],

  // Day 3 — สิ่งมีชีวิต
  ['犬', '犭', 'หมา', 3, [['猫', 'แมว'], ['独', 'เดี่ยว']]],
  ['牛', '', 'วัว', 3, [['物', 'สิ่งของ'], ['特', 'พิเศษ']]],
  ['馬', '', 'ม้า', 3, [['駅', 'สถานีรถไฟ'], ['験', 'ทดลอง']]],
  ['鳥', '', 'นก', 3, [['鳴', 'ร้อง (เสียงสัตว์)'], ['島', 'เกาะ']]],
  ['魚', '', 'ปลา', 3, [['鮮', 'สด'], ['鯨', 'วาฬ']]],
  ['虫', '', 'แมลง', 3, [['蚊', 'ยุง'], ['蝶', 'ผีเสื้อ']]],
  ['羊', '', 'แกะ', 3, [['美', 'สวย'], ['洋', 'มหาสมุทร']]],
  ['竹', '', 'ไผ่', 3, [['笑', 'หัวเราะ'], ['答', 'คำตอบ']]],
  ['艹', '', 'หญ้า / พืช', 3, [['花', 'ดอกไม้'], ['草', 'หญ้า']]],
  ['禾', '', 'ต้นข้าว', 3, [['秋', 'ฤดูใบไม้ร่วง'], ['私', 'ฉัน']]],
  ['米', '', 'ข้าวสาร', 3, [['料', 'ค่าธรรมเนียม'], ['精', 'ประณีต']]],
  ['貝', '', 'หอย → เงิน', 3, [['買', 'ซื้อ'], ['財', 'ทรัพย์สิน']]],
  ['生', '', 'เกิด / มีชีวิต', 3, [['性', 'เพศ / ธรรมชาติ'], ['星', 'ดาว']]],
  ['肉', '月', 'เนื้อ', 3, [['育', 'เลี้ยงดู'], ['腐', 'เน่า']]],
  ['羽', '', 'ขนนก / ปีก', 3, [['習', 'เรียนรู้'], ['翌', 'ถัดไป']]],

  // Day 4 — ของที่มนุษย์ทำ
  ['刀', '刂', 'มีด / ดาบ', 4, [['分', 'แบ่ง'], ['別', 'แยก']]],
  ['弓', '', 'ธนู', 4, [['引', 'ดึง'], ['強', 'แข็งแรง']]],
  ['矢', '', 'ลูกศร', 4, [['知', 'รู้'], ['医', 'หมอ']]],
  ['戈', '', 'หอก', 4, [['戦', 'สงคราม'], ['成', 'สำเร็จ']]],
  ['斤', '', 'ขวาน', 4, [['新', 'ใหม่'], ['近', 'ใกล้']]],
  ['車', '', 'รถ / ยานพาหนะ', 4, [['転', 'หมุน'], ['軽', 'เบา']]],
  ['舟', '', 'เรือ', 4, [['船', 'เรือใหญ่'], ['航', 'เดินเรือ']]],
  ['糸', '', 'ด้าย', 4, [['紙', 'กระดาษ'], ['線', 'เส้น']]],
  ['衣', '衤', 'เสื้อผ้า', 4, [['初', 'ครั้งแรก'], ['複', 'ซ้อน']]],
  ['巾', '', 'ผ้า', 4, [['帽', 'หมวก'], ['席', 'ที่นั่ง']]],
  ['皿', '', 'จาน', 4, [['盛', 'ตัก / เจริญ'], ['益', 'ประโยชน์']]],
  ['食', '飠', 'อาหาร', 4, [['飲', 'ดื่ม'], ['館', 'อาคาร']]],
  ['酉', '', 'ไหเหล้า', 4, [['酒', 'เหล้า'], ['配', 'แจกจ่าย']]],
  ['工', '', 'งาน / เครื่องมือ', 4, [['左', 'ซ้าย'], ['功', 'ความสำเร็จ']]],
  ['皮', '', 'หนัง', 4, [['波', 'คลื่น'], ['彼', 'เขา (ผู้ชาย)']]],

  // Day 5 — สถานที่ / สิ่งปลูกสร้าง
  ['宀', '', 'หลังคาบ้าน', 5, [['家', 'บ้าน'], ['安', 'ปลอดภัย']]],
  ['广', '', 'เพิงพิงผา', 5, [['店', 'ร้าน'], ['広', 'กว้าง']]],
  ['厂', '', 'หน้าผา', 5, [['原', 'ที่ราบ / ต้นกำเนิด'], ['厚', 'หนา']]],
  ['門', '', 'ประตูใหญ่', 5, [['開', 'เปิด'], ['間', 'ระหว่าง']]],
  ['戸', '', 'ประตูบานเดียว', 5, [['所', 'สถานที่'], ['扉', 'บานประตู']]],
  ['穴', '', 'รู / ถ้ำ', 5, [['空', 'ท้องฟ้า / ว่าง'], ['究', 'ค้นคว้า']]],
  ['里', '', 'หมู่บ้าน', 5, [['野', 'ทุ่ง'], ['量', 'ปริมาณ']]],
  ['阝', '', 'เนินเขา (ซ้าย) / เมือง (ขวา)', 5, [['院', 'สถาบัน'], ['部', 'แผนก']]],
  ['囗', '', 'กรอบล้อมรอบ', 5, [['国', 'ประเทศ'], ['園', 'สวน']]],
  ['高', '', 'สูง', 5, [['稿', 'ต้นฉบับ']]],
  ['井', '', 'บ่อน้ำ', 5, [['囲', 'ล้อมรอบ']]],
  ['京', '', 'เมืองหลวง', 5, [['景', 'ทิวทัศน์'], ['涼', 'เย็นสบาย']]],
  ['尸', '', 'ศพ / ตัวบ้าน', 5, [['屋', 'อาคาร'], ['局', 'สำนักงาน']]],
  ['冂', '', 'กรอบเปิดด้านล่าง', 5, [['内', 'ข้างใน'], ['円', 'วงกลม / เยน']]],
  ['谷', '', 'หุบเขา', 5, [['浴', 'อาบน้ำ'], ['容', 'บรรจุ']]],

  // Day 6 — การกระทำ
  ['言', '', 'คำพูด', 6, [['話', 'พูด'], ['語', 'ภาษา']]],
  ['辶', '', 'เดินทาง / ถนน', 6, [['道', 'ถนน'], ['送', 'ส่ง']]],
  ['彳', '', 'ก้าวเดิน', 6, [['行', 'ไป'], ['待', 'รอ']]],
  ['攵', '', 'ตี / ทำให้เกิด', 6, [['教', 'สอน'], ['数', 'ตัวเลข']]],
  ['力', '', 'พลัง / แรง', 6, [['動', 'เคลื่อนไหว'], ['助', 'ช่วย']]],
  ['又', '', 'มือ / อีกครั้ง', 6, [['友', 'เพื่อน'], ['取', 'หยิบ']]],
  ['寸', '', 'มือ / หน่วยนิ้ว', 6, [['対', 'ตรงข้าม'], ['村', 'หมู่บ้าน']]],
  ['爪', '', 'เล็บ / มือคว้า', 6, [['受', 'รับ'], ['愛', 'รัก']]],
  ['見', '', 'มองเห็น', 6, [['親', 'พ่อแม่'], ['観', 'สังเกต']]],
  ['立', '', 'ยืน', 6, [['音', 'เสียง'], ['産', 'ผลิต']]],
  ['止', '', 'หยุด', 6, [['歩', 'เดิน'], ['正', 'ถูกต้อง']]],
  ['走', '', 'วิ่ง', 6, [['起', 'ลุกขึ้น'], ['越', 'ข้าม']]],
  ['入', '', 'เข้า', 6, [['込', 'ยัดเข้าไป'], ['全', 'ทั้งหมด']]],
  ['出', '', 'ออก', 6, [['拙', 'งุ่มง่าม']]],
  ['白', '', 'ขาว', 6, [['的', 'เป้าหมาย'], ['百', 'ร้อย']]],

  // Day 7 — ชิ้นส่วนเล็ก
  ['一', '', 'หนึ่ง', 7, [['三', 'สาม'], ['上', 'บน']]],
  ['二', '', 'สอง', 7, [['元', 'ต้นกำเนิด'], ['互', 'ซึ่งกันและกัน']]],
  ['十', '', 'สิบ', 7, [['古', 'เก่า'], ['早', 'เช้า / เร็ว']]],
  ['八', '', 'แปด / แยกออก', 7, [['分', 'แบ่ง'], ['公', 'สาธารณะ']]],
  ['丶', '', 'จุด', 7, [['主', 'เจ้าของ'], ['丸', 'กลม']]],
  ['丿', '', 'ขีดเฉียง', 7, [['千', 'พัน'], ['乗', 'ขึ้น (ยานพาหนะ)']]],
  ['冫', '', 'น้ำแข็ง', 7, [['冷', 'เย็น'], ['凍', 'แข็งตัว']]],
  ['冖', '', 'ฝาครอบ', 7, [['写', 'ถ่าย / คัดลอก'], ['軍', 'กองทัพ']]],
  ['亠', '', 'ฝาหม้อ', 7, [['京', 'เมืองหลวง'], ['高', 'สูง']]],
  ['勹', '', 'ห่อ / โอบ', 7, [['包', 'ห่อ'], ['句', 'วลี']]],
  ['厶', '', 'ส่วนตัว', 7, [['私', 'ฉัน'], ['参', 'เข้าร่วม']]],
  ['中', '', 'กลาง', 7, [['仲', 'ความสัมพันธ์'], ['忠', 'ภักดี']]],
  ['王', '', 'ราชา / หยก', 7, [['理', 'เหตุผล'], ['現', 'ปรากฏ']]],
  ['弋', '', 'หลัก / เสา', 7, [['式', 'พิธี / แบบแผน'], ['代', 'แทน']]],
  ['疒', '', 'โรค', 7, [['病', 'ป่วย'], ['痛', 'เจ็บ']]],

  /*
   * Day 8 — เก็บตกตัวที่บล็อกการแตกคันจิมากที่สุด
   *
   * คัดจากข้อมูลจริง ไม่ได้เลือกตามความรู้สึก: ต้องเป็น Kangxi radical
   * ที่มีความหมายผูกเรื่องได้ และ KRADFILE ต้องใช้จริงในคันจิ jōyō
   * ตัวอย่างที่ตกเกณฑ์ — 曰 กับ 网 ไม่เคยถูกใช้เลย · 行 ใช้แค่ 7 ตัว
   * (ตัวเลขท้ายคำอธิบายคือจำนวนคันจิ jōyō ที่มีชิ้นส่วนนั้น)
   */
  ['儿', '', 'ขาคน (สองขาที่ยืนอยู่)', 8, [['見', 'เห็น'], ['発', 'ออก / เริ่ม']]],
  ['幺', '', 'ด้ายเส้นจิ๋ว → เล็ก / ลึกลับ', 8, [['後', 'ข้างหลัง'], ['経', 'ผ่าน / คัมภีร์']]],
  ['匕', '', 'ช้อน / คนก้มตัว', 8, [['化', 'เปลี่ยนรูป'], ['北', 'ทิศเหนือ']]],
  ['夂', '', 'เท้าที่ก้าวลงมา → มาถึงช้า', 8, [['各', 'แต่ละ'], ['終', 'จบ']]],
  ['隹', '', 'นกหางสั้น', 8, [['集', 'รวมกัน'], ['進', 'ก้าวหน้า']]],
  ['卩', '㔾', 'คนคุกเข่า / ตราประทับ', 8, [['命', 'ชีวิต / คำสั่ง'], ['危', 'อันตราย']]],
  ['乙', '乚', 'สิ่งที่งอ / ลำดับที่สอง', 8, [['電', 'ไฟฟ้า'], ['乱', 'วุ่นวาย']]],
  ['示', '礻', 'แท่นบูชา → เทพเจ้า / แสดงให้เห็น', 8, [['票', 'บัตร'], ['察', 'สังเกต']]],
  ['欠', '', 'คนอ้าปาก → ขาด / หาว', 8, [['次', 'ถัดไป'], ['歌', 'เพลง']]],
  ['殳', '', 'อาวุธด้ามยาว → ตี', 8, [['投', 'ขว้าง'], ['段', 'ขั้น / ระดับ']]],
]

const DAY_LABEL: Record<number, string> = {
  1: 'D1 ธรรมชาติ',
  2: 'D2 ร่างกาย',
  3: 'D3 สิ่งมีชีวิต',
  4: 'D4 ของใช้',
  5: 'D5 สถานที่',
  6: 'D6 การกระทำ',
  7: 'D7 ชิ้นเล็ก',
  8: 'D8 เก็บตก',
}

const cards: Card[] = DATA.map(([c, v, t, d, x]) => ({
  id: `bushu:${c}`,
  deck: 'bushu' as const,
  tags: [`d${d}`],
  front: c,
  back: t,
  extra: {
    variant: v || undefined,
    examples: x.map(([kanji, meaning]) => ({ kanji, meaning })),
  },
}))

const groups: Group[] = Object.entries(DAY_LABEL).map(([d, label]) => ({
  tag: `d${d}`,
  label,
  section: 'ชุด',
}))

export const bushuDeck: Deck = {
  id: 'bushu',
  titleJa: '部首ドリル',
  titleTh: `ฝึกจำ radical ${DATA.length} ตัว · Phase 0`,
  // D1–D7 คือ 105 ตัวตามแผนใน NOTES.md · D8 เพิ่มทีหลัง เลือกเรียนหรือไม่ก็ได้
  modes: ['auto', 'lesson', 'glyph-to-meaning', 'meaning-to-glyph', 'recall'],
  scheduling: 'srs',
  cards,
  groups,
  gridSection: 'ชุด',
}
